/** Shared dashboard SMTP + Interspire chart lookback. */

export const DASHBOARD_CHART_RANGES = ['24m', '12m', '3m', '30d', '7d', '24h'] as const;

export type DashboardChartRange = (typeof DASHBOARD_CHART_RANGES)[number];

export const DEFAULT_DASHBOARD_CHART_RANGE: DashboardChartRange = '24m';

const STORAGE_KEY = 'emailcraft_dashboard_chart_range';

export const DASHBOARD_CHART_RANGE_OPTIONS: Array<{
  value: DashboardChartRange;
  label: string;
  shortLabel: string;
}> = [
  { value: '24m', label: '24 months', shortLabel: '24 mo' },
  { value: '12m', label: '12 months', shortLabel: '12 mo' },
  { value: '3m', label: '3 months', shortLabel: '3 mo' },
  { value: '30d', label: '30 days', shortLabel: '30 d' },
  { value: '7d', label: '7 days', shortLabel: '7 d' },
  { value: '24h', label: '24 hours', shortLabel: '24 h' },
];

export function isDashboardChartRange(v: unknown): v is DashboardChartRange {
  return typeof v === 'string' && (DASHBOARD_CHART_RANGES as readonly string[]).includes(v);
}

/** Accept legacy `1m` (calendar month) as rolling 30 days. */
export function parseDashboardChartRange(raw: unknown): DashboardChartRange {
  if (raw === '1m') return '30d';
  return isDashboardChartRange(raw) ? raw : DEFAULT_DASHBOARD_CHART_RANGE;
}

/** Months for monthly ranges; null for day/hour ranges. */
export function monthsForDashboardRange(range: DashboardChartRange): number | null {
  switch (range) {
    case '24m':
      return 24;
    case '12m':
      return 12;
    case '3m':
      return 3;
    default:
      return null;
  }
}

export function labelForDashboardRange(range: DashboardChartRange): string {
  return DASHBOARD_CHART_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? range;
}

export function avgLegendForDashboardRange(range: DashboardChartRange): string {
  switch (range) {
    case '24m':
      return '24-mo avg';
    case '12m':
      return '12-mo avg';
    case '3m':
      return '3-mo avg';
    case '30d':
      return '30-day avg';
    case '7d':
      return '7-day avg';
    case '24h':
      return '24h avg';
    default:
      return 'Period avg';
  }
}

export function readStoredDashboardChartRange(): DashboardChartRange {
  try {
    return parseDashboardChartRange(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_DASHBOARD_CHART_RANGE;
  }
}

export function storeDashboardChartRange(range: DashboardChartRange): void {
  try {
    localStorage.setItem(STORAGE_KEY, range);
  } catch {
    /* ignore */
  }
}
